import { useEffect, useState } from 'react';
import { useForm, UseFormReturnType } from '@mantine/form';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import {
  FormAccordion,
  FormAccordionItem,
  ReadOnlyWrapper,
} from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { Grid } from '@mantine/core';
import { useParams, useSearchParams } from 'react-router';
import {
  getFinancialFormSetupValues,
  ProductFormComponentProps,
  updateFeedInfoAfterAssetCreation,
  validateFundHoldingsAndDisplayError,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormHelpers';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import { DynamicUiProviderWrapper } from 'src/financial/financial-data/financial-item/forms/common/DynamicUiProviderWrapper';
import { FinancialFormField } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormField';
import { GridPair } from 'src/financial/financial-data/financial-item/forms/common/GridPair';
import {
  FUND_HOLDING,
  FundHoldingFormAccordionItemList,
} from 'src/financial/financial-data/financial-item/forms/common/FundHoldingFormAccordionItemList';
import { ContributionFormAccordionItemList } from 'src/financial/financial-data/financial-item/forms/common/ContributionFormAccordionItemList';
import {
  INCOME_WITHDRAWAL,
  IncomeWithdrawalFormAccordionItemList,
} from 'src/financial/financial-data/financial-item/forms/common/IncomeWithdrawalFormAccordionItemList';
import {
  BENEFICIARY,
  BeneficiaryFormAccordionItemList,
} from 'src/financial/financial-data/financial-item/forms/common/BeneficiaryFormAccordionItemList';
import { FeeFormAccordionItemList } from 'src/financial/financial-data/financial-item/forms/common/FeeFormAccordionItemList';
import {
  FeedInfo,
  FinancialFormContainer,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormContainer';
import {
  FormTitleWithBadges,
  useConnectedDataIcon,
  useCurrencyConversionIcon,
} from 'src/financial/financial-data/financial-item/forms/common/ConnectedDataBadge';
import {
  useValidateProviderReference,
  validateProviderReferenceAndSetError,
} from 'src/financial/financial-data/hooks/useValidateProviderReference';
import {
  scrollToElementWithError,
  validateContributionEndDate,
} from 'src/financial/financial-data/financial-item/forms/common/validationUtils';

const INCOME_DRAWDOWN = 'IncomeDrawdownPension';
const CONTRIBUTION = 'DcPensionContribution';
const FEE_TYPE = 'InvestmentAndPensionFee';

export function IncomeDrawdown(props: ProductFormComponentProps) {
  const { onSave } = props;
  const [searchParams] = useSearchParams();
  const { clientId = '' } = useParams<{ clientId: string }>();
  const { validateProviderReference } = useValidateProviderReference(clientId);

  const {
    isCreateMode,
    uiData,
    initialValues,
    onDeleteConfirm,
    feedInfo: initialFeedInfo,
    onConnectAsset,
  } = getFinancialFormSetupValues(INCOME_DRAWDOWN, props, [
    { product: FUND_HOLDING, field: 'fund_holdings' },
    { product: INCOME_WITHDRAWAL, field: 'income_withdrawals' },
    { product: CONTRIBUTION, field: 'contributions' },
    { product: BENEFICIARY, field: 'beneficiaries' },
    { product: FEE_TYPE, field: 'fees' },
  ]);

  const [feedInfo, setFeedInfo] = useState<FeedInfo>(initialFeedInfo);

  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    initialValues: initialValues,
    validate: {
      [CONTRIBUTION]: {
        end_date: (value, values, path) => {
          return validateContributionEndDate(CONTRIBUTION, value, values, path);
        },
      },
    },
  });

  useEffect(() => {
    const providerReference = searchParams.get('providerReference');
    if (providerReference && isCreateMode) {
      form.setFieldValue(`${INCOME_DRAWDOWN}.provider_reference_number`, providerReference);
    }
  }, [searchParams, isCreateMode, form]);

  const handleSave = async (values: FormValuesType) => {
    if (
      FUND_HOLDING in values &&
      Array.isArray(values[FUND_HOLDING]) &&
      INCOME_WITHDRAWAL in values &&
      Array.isArray(values[INCOME_WITHDRAWAL]) &&
      CONTRIBUTION in values &&
      Array.isArray(values[CONTRIBUTION]) &&
      BENEFICIARY in values &&
      Array.isArray(values[BENEFICIARY]) &&
      FEE_TYPE in values &&
      Array.isArray(values[FEE_TYPE])
    ) {
      const incomeDrawdownData = values[INCOME_DRAWDOWN] as Record<string, unknown>;

      const isProviderReferenceValid = await validateProviderReferenceAndSetError(
        incomeDrawdownData,
        clientId,
        form,
        INCOME_DRAWDOWN,
        validateProviderReference,
      );
      if (!isProviderReferenceValid) {
        return;
      }

      if (!validateFundHoldingsAndDisplayError(values, form)) {
        return;
      }

      const mappedValues = {
        ...incomeDrawdownData,
        fund_holdings: values[FUND_HOLDING].map(item => ({ ...item })),
        income_withdrawals: values[INCOME_WITHDRAWAL].map(item => ({ ...item })),
        contributions: values[CONTRIBUTION].map(item => ({ ...item })),
        beneficiaries: values[BENEFICIARY].map(item => ({ ...item })),
        fees: values[FEE_TYPE].map(item => ({ ...item })),
      };

      await onSave(mappedValues, uiData.ui.entitiesMetadata[INCOME_DRAWDOWN]);

      updateFeedInfoAfterAssetCreation(form, INCOME_DRAWDOWN, isCreateMode, setFeedInfo);
    }
  };

  return (
    <DynamicUiProviderWrapper
      form={form}
      uiData={uiData}
      onDeleteConfirm={isCreateMode ? () => {} : onDeleteConfirm}
      isCreateMode={isCreateMode}
    >
      <form onSubmit={form.onSubmit(handleSave, scrollToElementWithError)} autoComplete={'off'}>
        <FormContent form={form} feedInfo={feedInfo} onConnect={onConnectAsset} />
      </form>
    </DynamicUiProviderWrapper>
  );
}

type FormContentProps = {
  form: UseFormReturnType<FormValuesType>;
  feedInfo: FeedInfo;
  onConnect?: (id: string) => void;
};

const FormContent = ({ form, feedInfo, onConnect }: FormContentProps) => {
  const { isEditing } = useDynamicUI();
  const connectedDataIcon = useConnectedDataIcon(feedInfo.isUpdatedAutomatically);

  const fundHoldings = form.getValues()[FUND_HOLDING];
  const hasCurrencyConversions =
    Array.isArray(fundHoldings) &&
    fundHoldings.some(
      (holding: { original_currency_code?: string | null }) => !!holding.original_currency_code,
    );
  const currencyConversionIcon = useCurrencyConversionIcon(hasCurrencyConversions);

  const summaryContent = (
    <Grid>
      <GridPair first={<FinancialFormField field="description" product={INCOME_DRAWDOWN} />} />
      <GridPair
        isHighlighted
        first={<FinancialFormField label="Provider" field="provider" product={INCOME_DRAWDOWN} />}
        second={<FinancialFormField field="provider_reference_number" product={INCOME_DRAWDOWN} />}
      />
      <GridPair
        isHighlighted
        first={<FinancialFormField label="Value" field="value" product={INCOME_DRAWDOWN} />}
        second={<FinancialFormField field="value_date" product={INCOME_DRAWDOWN} />}
      />
      <GridPair
        first={<FinancialFormField field="status" product={INCOME_DRAWDOWN} forceEditable />}
        second={
          <FinancialFormField
            field="is_serviced_by_advisor"
            product={INCOME_DRAWDOWN}
            forceEditable
          />
        }
      />
      <GridPair fullWidth first={<FinancialFormField field="notes" product={INCOME_DRAWDOWN} />} />
    </Grid>
  );

  const titleText = 'Assets > Pensions > Income drawdown';
  const titleWithBadges = (
    <FormTitleWithBadges
      title={titleText}
      showConnectedBadge={feedInfo.isUpdatedAutomatically}
      showCurrencyBadge={hasCurrencyConversions}
    />
  );

  const fundHoldingsSection = (
    <FundHoldingFormAccordionItemList
      isEditing={isEditing}
      form={form}
      productType={INCOME_DRAWDOWN}
      titleIcon={connectedDataIcon}
      currencyIcon={currencyConversionIcon}
      feedInfo={feedInfo}
    />
  );

  return (
    <FinancialFormContainer title={titleWithBadges} feedInfo={feedInfo} onConnect={onConnect}>
      <FormAccordion
        defaultValue={[
          'summary',
          'fund_holdings',
          'income_withdrawal',
          'contribution',
          'beneficiaries',
          'fee',
        ]}
      >
        <FormAccordionItem value="summary" label="Summary">
          {feedInfo.isUpdatedAutomatically ? (
            <ReadOnlyWrapper>{summaryContent}</ReadOnlyWrapper>
          ) : (
            summaryContent
          )}
        </FormAccordionItem>
        {fundHoldingsSection}
        <IncomeWithdrawalFormAccordionItemList isEditing={isEditing} form={form} />
        <ContributionFormAccordionItemList
          form={form}
          isEditing={isEditing}
          contributionType={CONTRIBUTION}
        />
        <FeeFormAccordionItemList form={form} isEditing={isEditing} feeType={FEE_TYPE} />
        <BeneficiaryFormAccordionItemList form={form} isEditing={isEditing} />
      </FormAccordion>
    </FinancialFormContainer>
  );
};
